import { Button, Stack, Typography } from "@mui/material";
import { useFormikWithoutDirtyForm } from "@shared";

import { FormikDatePicker } from "@/common/components/fields/formik/formik-date-picker/FormikDatePicker";
import { UserPermission } from "@/common/contexts/current-user-context/CurrentUserContextProvider.types";
import withAuthorization from "@/common/hocs/with-authorization/withAuthorization";
import { useTranslation } from "@/translations";

import { HolidayTable } from "./components/holiday-table/HolidayTable";
import { useHolidayImport } from "./hooks/useHolidayImport";

interface ImportFormValues {
  fromYear: Date;
  tillYear: Date;
}

const SELECTABLE_YEARS_AHEAD = 10;

function HolidaysSettingsInner() {
  const { t } = useTranslation();
  const { holidays, isLoading, triggerImport, isImporting } = useHolidayImport();

  const currentYear = new Date().getFullYear();
  const minDate = new Date(currentYear, 0, 1);
  const maxDate = new Date(currentYear + SELECTABLE_YEARS_AHEAD, 0, 1);

  const formik = useFormikWithoutDirtyForm<ImportFormValues>({
    initialValues: { fromYear: new Date(currentYear, 0, 1), tillYear: new Date(currentYear, 0, 1) },
    onSubmit: (values) =>
      triggerImport({ fromYear: values.fromYear.getFullYear(), tillYear: values.tillYear.getFullYear() }),
  });

  const handleFromYearChange = (date: Date | null) => {
    const fromDate = date ?? new Date(currentYear, 0, 1);
    void formik.setFieldValue("fromYear", fromDate);
    if (formik.values.tillYear.getFullYear() < fromDate.getFullYear()) {
      void formik.setFieldValue("tillYear", fromDate);
    }
  };

  return (
    <Stack gap={3} sx={{ flex: 1, minHeight: 0 }} bgcolor="white">
      <Stack gap={1}>
        <Typography variant="h5">{t("settings.holidays.title")}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t("settings.holidays.description")}
        </Typography>
      </Stack>

      <form onSubmit={formik.handleSubmit}>
        <Stack direction="row" alignItems="flex-start" spacing={2} flexWrap="wrap" useFlexGap>
          <FormikDatePicker
            formik={formik}
            fieldName="fromYear"
            label={t("settings.holidays.fromYear")}
            views={["year"]}
            openTo="year"
            onChange={handleFromYearChange}
            minDate={minDate}
            maxDate={maxDate}
            formControlProps={{ fullWidth: false, sx: { width: 140 } }}
          />
          <FormikDatePicker
            formik={formik}
            fieldName="tillYear"
            label={t("settings.holidays.tillYear")}
            views={["year"]}
            openTo="year"
            minDate={formik.values.fromYear}
            maxDate={maxDate}
            formControlProps={{ fullWidth: false, sx: { width: 140 } }}
          />
          <Button type="submit" variant="contained" disabled={isImporting} sx={{ height: 40 }}>
            {isImporting ? t("settings.holidays.importing") : t("settings.holidays.importButton")}
          </Button>
        </Stack>
      </form>

      <Stack flex={1} minHeight={0} sx={{ overflow: "auto" }}>
        <HolidayTable holidays={holidays} isLoading={isLoading} />
      </Stack>
    </Stack>
  );
}

export const HolidaysSettings = withAuthorization(HolidaysSettingsInner, UserPermission.Supervisor);
